// Section 21 (stream G): the Ledger screen root, the only component in this folder that reads
// the store. It builds the view model from `game` (on the run's config), keeps the selected
// section and the "copied" note locally, and wires the actions: continueSandbox (store),
// closeOverlay for Back (unless `onBack` is given) and `onExport`, which the integration wires
// to Stream F's export action (the button is hidden without it). Mount it for ui.overlay
// 'ledger', which is also the win and loss screen. See README.md.
import { useComputed } from '@preact/signals';
import { useState } from 'preact/hooks';
import { closeOverlay, continueSandbox, game, runConfig } from '../../store';
import { ledgerVM, type LedgerSectionId } from '../../viewModels/ledger';
import { jobLabel } from '../../viewModels/people';
import { LedgerView } from './ledger-view';
import { summaryText } from './summary';
import './ledger.css';

export interface LedgerScreenProps {
  /** Export the run (Stream F's persist action and download), wired by M6. */
  onExport?: () => void;
  /** Leave the Ledger; defaults to closing the overlay. */
  onBack?: () => void;
}

function copy(text: string): Promise<void> {
  const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
  return clipboard === undefined ? Promise.resolve() : clipboard.writeText(text);
}

export function LedgerScreen({ onExport, onBack }: LedgerScreenProps = {}) {
  const vm = useComputed(() => (game.value === null ? null : ledgerVM(game.value, runConfig())));
  const [section, setSection] = useState<LedgerSectionId>(() =>
    game.value?.outcome === null ? 'people' : 'outcome',
  );
  const [copied, setCopied] = useState<'seed' | 'summary' | null>(null);
  const v = vm.value;
  if (v === null) return null;
  const copyAndNote = (what: 'seed' | 'summary', text: string): void => {
    void copy(text).then(() => setCopied(what));
  };
  return (
    <LedgerView
      vm={v}
      jobLabel={jobLabel}
      section={section}
      onSection={setSection}
      onContinueSandbox={() => continueSandbox()}
      {...(onExport === undefined ? {} : { onExport })}
      onCopySeed={() => copyAndNote('seed', v.outcome.seed)}
      onCopySummary={() => copyAndNote('summary', summaryText(v))}
      copied={copied}
      onBack={onBack ?? (() => closeOverlay())}
    />
  );
}
